<script setup lang="ts">
// Website-Rahmen (Kopf- und Fußzeile) für beide SPAs, wenn sie standalone
// laufen — also im Normalfall, seit die CMS-Seite auf die SPA weiterleitet
// bzw. SAP die DOI-Seiten direkt verlinkt. Im (alt-gecachten) Iframe liefert
// die Kundenseite den Rahmen selbst, dort rendert die Komponente nur den
// Slot. Die Erkennung ist ein einmaliger Fakt beim Laden.
//
// Gestaltung nach der relaunchten Website www.energis-netzgesellschaft.de
// (10/2026, TYPO3 bootstrap_package): weißer Header mit dem farbigen
// energis-Netz-Logo links (Höhen siehe Style-Block); unterste
// Footer-Leiste dunkelgrau #212121 mit weißen Links (Gewicht 500,
// ohne Unterstrich). Logo: fileadmin/bootstrap_logo_override/…/logo_energis_netz.svg.
//
// Ziele bewusst target="_blank": ein Same-Tab-Wechsel würde die laufende
// Erfassung verlassen (In-Memory-Store) und wäre im Offline-Einsatz
// (Keller) eine Sackgasse.
import { isStandalone } from "@shared/child/cos_child";
import { customerLinks } from "@shared/config/ene";
import logoUrl from "@shared/assets/energis-netz-logo.svg";

const standalone = isStandalone();

const WEBSITE_URL = "https://www.energis-netzgesellschaft.de/";
// Stand Relaunch 10/2026: Datenschutz, Impressum, Barrierefreiheit (kein
// Disclaimer mehr, keine .html-Endungen). "Barrierefreiheit" zeigt bewusst
// auf die BFSG-Erklärung DIESER Anwendung, nicht auf die der Website.
const footerLinks = [
  { label: "Datenschutz", href: customerLinks.datenschutz },
  { label: "Impressum", href: `${WEBSITE_URL}impressum` },
  { label: "Barrierefreiheit", href: customerLinks.bfsg },
  { label: "www.energis-netzgesellschaft.de", href: WEBSITE_URL },
];
</script>

<template>
  <!-- Standalone: Flex-Spalte über die volle Viewport-Höhe, damit der
       Footer auch bei kurzen Seiten unten sitzt. Im Iframe keine
       Mindesthöhe — iframe-resizer misst die reine Inhaltshöhe. -->
  <div
    class="app-shell"
    :class="{ 'app-shell--standalone': standalone }"
    data-iframe-height
  >
    <header v-if="standalone" class="site-header">
      <div class="container d-flex align-items-center h-100">
        <a
          :href="WEBSITE_URL"
          target="_blank"
          rel="noopener"
          class="site-header__brand"
        >
          <img
            :src="logoUrl"
            alt="energis-Netzgesellschaft mbH"
            class="site-header__logo"
          />
        </a>
      </div>
    </header>

    <slot />

    <footer v-if="standalone" class="site-footer">
      <nav
        class="container d-flex flex-wrap justify-content-center column-gap-4 row-gap-1"
        aria-label="Rechtliche Informationen"
      >
        <a
          v-for="link in footerLinks"
          :key="link.href"
          :href="link.href"
          target="_blank"
          rel="noopener"
        >
          {{ link.label }}
        </a>
      </nav>
    </footer>
  </div>
</template>

<style scoped>
.app-shell--standalone {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  /* $body-bg ist für den Iframe-Fall transparent; standalone braucht die
     Seite einen eigenen weißen Grund wie die Website. */
  background-color: #ffffff;
}

/* Weißer Header wie auf der Website; die feine Linie trennt ihn vom
   ebenfalls weißen Inhalt. */
/* Desktop-Höhen wie die Website (--mainnavigation-nav-height 100/110 px,
   Logo dort Höhe × 0,65). Mobil bewusst größer als die Website (dort nur
   40 px Logo in 70 px — zu klein, Vorgabe Jörg 2026-10-07): 60 px Logo in
   80 px. Logo-Höhen ganzzahlig: krumme Pixelhöhen lassen die feinen Linien
   der SVG-Wortmarke weich wirken. */
.site-header {
  height: 80px;
  background-color: #ffffff;
  border-bottom: 1px solid #dedede;
}
.site-header__brand {
  display: inline-flex;
}
.site-header__logo {
  /* Seitenverhältnis 438.2 × 176.4 — width: auto hält die Proportion. */
  height: 60px;
  width: auto;
  max-width: 100%;
}
@media (min-width: 992px) {
  .site-header {
    height: 100px;
  }
  .site-header__logo {
    height: 65px;
  }
}
@media (min-width: 1200px) {
  .site-header {
    height: 110px;
  }
  .site-header__logo {
    height: 72px;
  }
}

/* Unterste Footer-Leiste der Website: dunkelgrau, weiße Links. */
.site-footer {
  background-color: #212121;
  padding: 1rem 0;
  margin-top: 2.5rem;
  font-size: 0.875rem;
}
.site-footer a {
  color: #ffffff;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
}
.site-footer a:hover,
.site-footer a:focus-visible {
  text-decoration: underline;
}
</style>
